// PhotoSwipeCore.js

import { useEffect } from "preact/hooks";
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import i18n from '@i18n';
//import 'photoswipe/style.css';

import {
  selector,
  infos_to_display,
  backdrop_opacity,
  theme,
  animation_toggle,
  separate_galleries,
  animation_speed,
  animation_speeds,
  right_click_protection,
  disable_arrows_on_mobile,
  slideshow,
  download,
  deep_linking,
  deep_linking_slug,
  social_sharing,
  map as location_map,
  magnification,
  full_screen,
  include_orphans,
  anti_selector,
  metadata_toggle,
} from "../settings";

import {
  transformAnimationType,
  findOrphanImages,
  mwl_log,
} from './utils';

import { transformMwlData } from '../metadata/exifFields';

import { initCaption } from './initCaption';
import { initFlatCaption } from './initFlatCaption';
import { initActionsContainer } from './initActionsContainer';
import { initSlideshow } from './initSlideshow';
import { initDownloadButton } from './initDownloadButton';
import { initCloseButton } from './initCloseButton';
import { initNavigationArrows } from './initNavigationArrows';
import { initRightClickProtection } from './initRightClickProtection';
import { initDeepLinking } from './initDeepLinking';
import { initSocialSharing } from './initSocialSharing';
import { initMapFunctionality } from './initMapFunctionality';
import { initMetadataToggle } from './initMetadataToggle';
import { initFullscreenButton } from './initFullscreenButton';

const mwlNamespace = 'mwl_pswp';

export default function PhotoSwipeCore() {

  const update_aria_label = ( img ) => {
    const current_aria = img.getAttribute('aria-label') || '';
    const alt_text = img.getAttribute('alt') || '';

    let aria = current_aria ? `${i18n.ARIA.ZOOM_IMAGE}: ${current_aria}` : i18n.ARIA.ZOOM_IMAGE;
    if ( alt_text ) aria += ` (${alt_text})`;
    
    img.setAttribute('aria-label', aria);
  }

  // Helper function to create a PhotoSwipe instance with common configuration
  const createPhotoSwipeInstance = (data, className, galleryElement = null, galleryIndex = null) => {

    const lightbox = new PhotoSwipeLightbox({
      bgOpacity: backdrop_opacity / 100,
      mainClass: 'mwl mwl-theme-' + theme + (galleryIndex !== null ? ` mwl__pswp--gallery-${galleryIndex}` : ''),
      pswpClassName: className,
      dataSource: data,
      showHideAnimationType: transformAnimationType(animation_toggle),
      showAnimationDuration: animation_speeds[animation_speed],
      hideAnimationDuration: animation_speeds[animation_speed],
      paddingFn: (viewportSize) => {
        const isSmallViewport = viewportSize.x < 600;

        if (isSmallViewport) {
          return {
            top: 20, bottom: 20, left: 0, right: 0
          }
        }
        
        return {
          top: 30, bottom: 30, left: 70, right: 70
        }
      },
      pswpModule: () => import(/* webpackChunkName: "photoswipe" */ 'photoswipe'),
      // Disable zoom on click when magnification is not enabled
      ...(magnification.enabled ? {} : {
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 'fit',
        maxZoomLevel: 1,
      }),
    });

    // Filters for thumbEl and placeholderSrc
    lightbox.addFilter('thumbEl', (thumbEl, data) => {
      const searchContext = galleryElement || document;
      let el = searchContext.querySelector(`[data-mwl-img-id='${data.id}']`);

      return el || thumbEl;
    });

    lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
      const searchContext = galleryElement || document;
      let el = searchContext.querySelector(`[data-mwl-img-id='${slide.data.id}']`);

      return el ? el.src : placeholderSrc;
    });

    // Prevent scrolling while keeping the scrollbar visible
    let scrollY = 0;
    const preventScroll = (e) => {
      window.scrollTo(0, scrollY);
    };

    // Check if device is mobile (width < 768px)
    const isMobile = () => window.innerWidth < 768;
    const shouldAutoFullscreen = () => isMobile() ? full_screen.start_mobile : full_screen.start_desktop;

    lightbox.on('beforeOpen', () => {
      scrollY = window.scrollY;
      window.addEventListener('scroll', preventScroll);
      // Auto-enter fullscreen if enabled (with Safari support)
      if (shouldAutoFullscreen()) {
        const el = document.documentElement;
        const requestFS = el.requestFullscreen || el.webkitRequestFullscreen;
        if (requestFS) {
          requestFS.call(el).catch(() => {
            // Fullscreen request failed
          });
        }
      }
    });

    lightbox.on('close', () => {
      window.removeEventListener('scroll', preventScroll);
      // Exit fullscreen if we auto-entered it (with Safari support)
      if (shouldAutoFullscreen() && (document.fullscreenElement || document.webkitFullscreenElement)) {
        const exitFS = document.exitFullscreen || document.webkitExitFullscreen;
        if (exitFS) {
          exitFS.call(document).catch(() => {});
        }
      }
    });
    
    // Store pswp instance globally for access during re-render (works for all lightboxes)
    lightbox.on('firstUpdate', () => {
      window.mwl_pswp = lightbox.pswp;
    });
    
    // Hide loading overlay when any lightbox opens (for reopen after rebuild)
    lightbox.on('openingAnimationEnd', () => {
      const overlay = document.getElementById('mwl-loading-overlay');
      if (overlay) {
        overlay.remove();
        window.mwlReopenSlideIndex = null;
      }
    });

    // Support for Meow Gallery "load more" functionality, on change event if we are on a .mgl-item and we are on the last slide, try to trigger load more with window.mgl_load_more( element  )
    lightbox.on('change', () => {

      if( window.mgl_load_more === undefined ) return; 

      const currentIndex   = lightbox.pswp.currIndex;

      // With separate galleries, we have a specific gallery element
      const element = galleryElement;
      if( element ){
        if( !element.classList.contains('mgl-gallery') ) return;
        const totalItems     = lightbox.pswp.getNumItems();
        const shouldLoadMore = ( totalItems - currentIndex ) <= 3;
        
        if( !shouldLoadMore ) return;

        window.mgl_load_more( element );
        return;
      }

      //Without separate galleries, we try to find the closest gallery container for the current slide
      const currentSlide = lightbox.pswp.currSlide;
      if( !currentSlide ) return;

      const currentSlideElement = document.querySelector(`[data-mwl-img-id='${currentSlide.data.id}']`);
      if( !currentSlideElement ) return;
      const galleryContainer = currentSlideElement.closest('.mgl-gallery');
      if( !galleryContainer ) return;

      const batchSize = 12; // Assuming Meow Gallery loads 12 images at a time
      const shouldLoadMore = currentIndex > 0 && ( currentIndex % batchSize ) >= ( batchSize - 3 );

      if( !shouldLoadMore ) return;
      
      window.mgl_load_more( galleryContainer );

    });

    // Add bottom hover zone for revealing collapsed metadata
    lightbox.on('firstUpdate', () => {
      if(theme.includes('flat')) return; // Skip for flat theme

      const wrapper = lightbox.pswp.element;
      if (wrapper && !wrapper.querySelector('.mwl__bottom-hover-zone')) {
        const hoverZone = document.createElement('div');
        hoverZone.className = 'mwl__bottom-hover-zone';
        wrapper.appendChild(hoverZone);
      }
    });

    // Initialize features
    if (theme.includes('flat')) {
      initFlatCaption(lightbox);
    } else {
      initCaption(lightbox, infos_to_display, transformAnimationType(animation_toggle), animation_speeds[animation_speed]);
    }
    initActionsContainer(lightbox);
    initCloseButton(lightbox);
    initNavigationArrows(lightbox, disable_arrows_on_mobile);
    if (slideshow.enabled) initSlideshow(lightbox, slideshow);
    if (download.enabled) initDownloadButton(lightbox);
    if (full_screen.enabled) initFullscreenButton(lightbox);
    if (right_click_protection) initRightClickProtection(lightbox);
    if (deep_linking) initDeepLinking(lightbox, data, deep_linking_slug);
    if (deep_linking && social_sharing) initSocialSharing(lightbox);
    if (location_map.enabled) initMapFunctionality(lightbox);
    if (metadata_toggle.enabled) initMetadataToggle(lightbox, metadata_toggle);

    lightbox.init();
  
    
    return lightbox;
  };

  useEffect(() => {
    // Dynamically import PhotoSwipe CSS only if it hasn't been loaded already (photoswipe/style.css)
    if (!document.querySelector('link[href*="photoswipe"]')) {
      import(/* webpackChunkName: "photoswipe-css" */ 'photoswipe/style.css');
    } else {
      console.warn('PhotoSwipe CSS already loaded. It seems like another Lightbox plugin is active. Please ensure only one Lightbox plugin is active to avoid conflicts.');
    }

    let transformedData = transformMwlData(window.mwl_data);

    // If include_orphans is enabled, find and add orphan images
    if (include_orphans) {
      const orphanImages = findOrphanImages(selector, anti_selector, window.mwl_data);
      if (orphanImages.length > 0) {
        transformedData = [...transformedData, ...orphanImages];
      }
    }

    if (!transformedData.length) {
      console.warn('(Meow Lightbox PS) No images found.');
      return;
    }
    //Make sure the selectors are not empty
    let selectorToUse = selector;
    if (!selectorToUse || selectorToUse.trim() === '') {
      selectorToUse = '.entry-content, .gallery, .mgl-gallery, .wp-block-gallery,  .wp-block-image, .gmap-image-marker'; //Fallback selector
    }

    // Query DOM once for all images (PHP already handled the selection)
    const allImagesInDOM = document.querySelectorAll('img[data-mwl-img-id]');
    
    // Create an array of image IDs in DOM order
    const domOrderIds = [];
    allImagesInDOM.forEach(img => {
      const id = img.dataset.mwlImgId;
      if (id && !domOrderIds.includes(id)) {
        domOrderIds.push(id);
      }
    });
    
    // Sort transformedData to match DOM order
    transformedData.sort((a, b) => {
      const indexA = domOrderIds.indexOf(String(a.id));
      const indexB = domOrderIds.indexOf(String(b.id));
      // If not found in DOM, keep at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    let lightboxes = [];
    let cleanupListeners = [];

    try {
      if (separate_galleries) {
        // Create separate PhotoSwipe instances for each gallery
        let gallerySelector = selector;
        if (!gallerySelector || gallerySelector.trim() === '') {
          gallerySelector = '.entry-content, .gallery, .mgl-gallery, .wp-block-gallery,  .wp-block-image, .gmap-image-marker'; 
        }
        let allContainers = Array.from(document.querySelectorAll(gallerySelector));

        // Separate into leaf galleries (no child galleries) and parent containers
        const leafGalleries = allContainers.filter(container => {
          return !allContainers.some(other => other !== container && container.contains(other));
        });
        
        // Track which image IDs have been processed
        const processedImageIds = new Set();
        
        // First, process all leaf galleries
        leafGalleries.forEach((gallery, galleryIndex) => {

          // Get images within this specific gallery
          const dataIdImages = gallery.querySelectorAll('img[data-mwl-img-id]');
          
          // Extract IDs efficiently
          const galleryIds = Array.from(dataIdImages).map(img => img.dataset.mwlImgId).filter(Boolean);
          
          // Filter data to only include images from this gallery that exist in transformedData
          // Use String() to ensure consistent comparison for both numeric and orphan IDs
          const galleryData = transformedData.filter(item => galleryIds.includes(String(item.id)));
          
          if (galleryData.length === 0) return;
          
          // Mark these IDs as processed
          galleryIds.forEach(id => processedImageIds.add(id));
          
          // Create PhotoSwipe instance for this gallery
          const lightbox = createPhotoSwipeInstance(
            galleryData, 
            `${mwlNamespace}-${galleryIndex}`, 
            gallery, 
            galleryIndex
          );
          
          lightboxes.push(lightbox);

          // Attach click event listeners only to images in this gallery
          // Process images with data-mwl-img-id attribute
          dataIdImages.forEach((img) => {
            const id = img.dataset.mwlImgId;
            if (id) {
              const index = galleryData.findIndex(item => item.id == id);
              if (index !== -1) {

                // Set aria label for accessibility
                update_aria_label(img);

                img.style.cursor = 'pointer';
                const onMouseEnter = () => { img.style.cursor = 'pointer'; };
                const onMouseLeave = () => { img.style.cursor = 'pointer'; };
                const onClick = (event) => {
                  event.preventDefault();
                  lightbox.loadAndOpen(index);
                };

                img.addEventListener('mouseenter', onMouseEnter);
                img.addEventListener('mouseleave', onMouseLeave);
                img.addEventListener('click', onClick);

                cleanupListeners.push(() => {
                  img.removeEventListener('mouseenter', onMouseEnter);
                  img.removeEventListener('mouseleave', onMouseLeave);
                  img.removeEventListener('click', onClick);
                });
              }
            }
          });
        });
        
        // Now handle any remaining images that weren't in leaf galleries (orphans in parent containers)
        const remainingImages = Array.from(allImagesInDOM).filter(img => {
          const id = img.dataset.mwlImgId;
          return id && !processedImageIds.has(id);
        });
        
        if (remainingImages.length > 0) {
          // Group remaining images - each one gets its own single-image lightbox
          remainingImages.forEach((img, idx) => {
            const id = img.dataset.mwlImgId;
            const imageData = transformedData.filter(item => String(item.id) === id);
            
            if (imageData.length === 0) return;
            
            const lightbox = createPhotoSwipeInstance(
              imageData,
              `${mwlNamespace}-orphan-${idx}`,
              null,
              null
            );
            
            lightboxes.push(lightbox);

            // Set aria label for accessibility
            update_aria_label(img);
            
            img.style.cursor = 'pointer';
            const onMouseEnter = () => { img.style.cursor = 'pointer'; };
            const onMouseLeave = () => { img.style.cursor = 'pointer'; };
            const onClick = (event) => {
              event.preventDefault();
              lightbox.loadAndOpen(0);
            };

            img.addEventListener('mouseenter', onMouseEnter);
            img.addEventListener('mouseleave', onMouseLeave);
            img.addEventListener('click', onClick);

            cleanupListeners.push(() => {
              img.removeEventListener('mouseenter', onMouseEnter);
              img.removeEventListener('mouseleave', onMouseLeave);
              img.removeEventListener('click', onClick);
            });
          });
        }
        
        // Check if we should reopen at a specific slide after rebuild (separate galleries mode)
        // For separate galleries, we open the first gallery at the saved index
        if (window.mwlReopenSlideIndex !== null && window.mwlReopenSlideIndex !== undefined && lightboxes.length > 0) {
          const reopenIndex = window.mwlReopenSlideIndex;
          const firstLightbox = lightboxes[0];
          // Ensure the index is within bounds of the first gallery's data
          const maxIndex = firstLightbox.options?.dataSource?.length ? firstLightbox.options.dataSource.length - 1 : 0;
          const safeIndex = Math.min(reopenIndex, maxIndex);
          mwl_log('🔓 Reopening lightbox (separate galleries) at slide', safeIndex);
          if (safeIndex >= 0) {
            setTimeout(() => {
              firstLightbox.loadAndOpen(safeIndex);
            }, 100);
          }
        }
      } else {
        // Original single lightbox for all images
        const lightbox = createPhotoSwipeInstance(transformedData, mwlNamespace);
        
        lightboxes.push(lightbox);
        window.pswpLightbox = lightbox;
        
        // Check if we should reopen at a specific slide after rebuild
        if (window.mwlReopenSlideIndex !== null && window.mwlReopenSlideIndex !== undefined) {
          const reopenIndex = window.mwlReopenSlideIndex;
          // Ensure the index is within bounds
          const safeIndex = Math.min(reopenIndex, transformedData.length - 1);
          mwl_log('🔓 Reopening lightbox at slide', safeIndex);
          if (safeIndex >= 0) {
            // Small delay to ensure lightbox is fully initialized
            setTimeout(() => {
              lightbox.loadAndOpen(safeIndex);
            }, 100);
          }
        }

        // Attach click event listeners to images - reuse allImagesInDOM
        allImagesInDOM.forEach((img) => {
          const id = img.dataset.mwlImgId;

          if (id) {
            const index = transformedData.findIndex(item => item.id == id);
            if (index !== -1) {

              // Set aria label for accessibility
              update_aria_label(img);

              img.style.cursor = 'pointer';
              const onMouseEnter = () => { img.style.cursor = 'pointer'; };
              const onMouseLeave = () => { img.style.cursor = 'pointer'; };
              const onClick = (event) => {
                event.preventDefault();
                lightbox.loadAndOpen(index);
              };

              img.addEventListener('mouseenter', onMouseEnter);
              img.addEventListener('mouseleave', onMouseLeave);
              img.addEventListener('click', onClick);

              cleanupListeners.push(() => {
                img.removeEventListener('mouseenter', onMouseEnter);
                img.removeEventListener('mouseleave', onMouseLeave);
                img.removeEventListener('click', onClick);
              });
            }
          }
        });
      }
    } catch (error) {
      console.error("Error initializing PhotoSwipe:", error);
    }

    return () => {
      cleanupListeners.forEach(cleanup => cleanup());
      lightboxes.forEach(lightbox => {
        if (lightbox) {
          lightbox.destroy();
        }
      });
    };
  }, []);

  return null;
}